/**
 * Draws one frame of the board: corridors, start/goal pads, the cursor dot,
 * and the HUD (mode, focus meter, telemetry, fps, level name). Corridors
 * are drawn straight from `GameState.corridors` (the hunt-mutated, current
 * shape), so the hunt pinch is visible for free — no separate "lean in"
 * rendering path needed, the walls really are narrower where the pinch is
 * biting.
 *
 * Not unit-tested: it's a thin, DOM/canvas-only presentation layer with no
 * decision logic of its own (see the task's test scope — pure helpers
 * only).
 */

import type { Level, Rect, Vec2 } from "../../src";
import { CURSOR_RADIUS } from "../../src";
import { levelBackdropPath, resolveAssetUrl, shouldUseFallback, wallTexturePath } from "./art";
import { getArtLoadState, getCachedImage, preloadImage } from "./artLoader";

const BG = "#08080c";
const WALL_FILL = "#1c1c28";
const WALL_STROKE = "rgba(140, 140, 190, 0.35)";
const START_FILL = "rgba(70, 200, 120, 0.28)";
const GOAL_FILL = "rgba(230, 190, 60, 0.32)";
const CURSOR_FILL = "#ff5d73";
const HUD_TEXT = "#d8d8e8";
const HUD_MUTED = "rgba(216, 216, 232, 0.7)";

export interface HudTelemetry {
  readonly saccades: number;
  readonly blinksPerMin: number;
  readonly headMotionDegPerS: number;
  readonly facePresentRatio: number;
}

/** Raw vs. calibrated gaze plus which `toEyeSample` path produced it this frame — see
 * `client/face/gaze.ts`'s `getLastGazePath`. Null when there's no current gaze sample
 * to show (MOUSE mode, or no face detected yet). */
export interface HudGaze {
  readonly raw: Vec2;
  readonly calibrated: Vec2;
  readonly path: "blendshapes" | "iris";
}

export type FocusStatus =
  | "ARMED"
  | "disarmed: unconfigured"
  | "disarmed: stale"
  | "disarmed: below threshold";

/** The Jev steering-assist channel's current state — null when it never ran this
 * frame (MOUSE mode, or disabled via `?steer=0`); see `client/game.ts`'s wiring. */
export interface HudSteer {
  readonly intent: "left" | "right" | "up" | "down" | "hold" | null;
  readonly confidence: number;
  readonly reliable: number;
  readonly assist: number;
  readonly damp: number;
  readonly reason: "none" | "stale" | "unreliable" | "hold" | "closed" | "assisting";
  /** ms since the current judgment was received; null when there's no judgment yet. */
  readonly ageMs: number | null;
}

/** The calibration-drift correction's accumulated offset and the steering channel's
 * current poll cadence — always present (drift defaults to zero, cadence has a slow
 * fallback) regardless of mode. */
export interface HudDrift {
  readonly offsetX: number;
  readonly offsetY: number;
  readonly pollMs: number;
}

export interface HudInfo {
  readonly mode: "EYES" | "MOUSE";
  readonly focusP: number | null;
  readonly focusThreshold: number;
  readonly focusStatus: FocusStatus;
  readonly gaze: HudGaze | null;
  readonly telemetry: HudTelemetry | null;
  readonly fps: number;
  readonly levelName: string;
  readonly banner: string | null;
  readonly steer: HudSteer | null;
  readonly drift: HudDrift;
  /** Which scare `ScareScene` is currently armed to show — `"video"` (the level-3
   * payoff) only once the operator's video is configured AND probed playable,
   * `"builtin"` otherwise (including as the video's own fallback). See
   * `client/scare/scene.ts`'s `ScareScene.armedMode`/`resolveArmedMode`. */
  readonly scareArmed: "video" | "builtin";
}

/** "+0.00" / "-0.03" style — `toFixed` already prefixes negatives with "-", this just
 * adds the "+" for zero/positive so the drift HUD line's sign is always explicit. */
function formatSigned(n: number): string {
  const v = n === 0 ? 0 : n; // normalize -0 away before the sign check
  return `${v >= 0 ? "+" : ""}${v.toFixed(2)}`;
}

/**
 * Kicks off (non-blocking) loads for `level`'s backdrop and the shared wall texture
 * under `artBaseUrl`, and reports whether each is actually ready to draw this frame.
 * Always attempts the preload — a cache hit is a cheap no-op (see `./artLoader`'s own
 * header comment) — so art that finishes loading between frames is picked up
 * automatically on the next `drawFrame` call, with no separate "art ready" event needed.
 */
function resolveArtForFrame(
  artBaseUrl: string,
  level: Level,
): { backdrop: HTMLImageElement | null; wallTexture: HTMLImageElement | null } {
  const backdropUrl = resolveAssetUrl(artBaseUrl, levelBackdropPath(level.theme));
  const wallUrl = resolveAssetUrl(artBaseUrl, wallTexturePath());
  preloadImage(backdropUrl);
  preloadImage(wallUrl);

  const backdrop = shouldUseFallback(getArtLoadState(backdropUrl))
    ? null
    : getCachedImage(backdropUrl);
  const wallTexture = shouldUseFallback(getArtLoadState(wallUrl)) ? null : getCachedImage(wallUrl);
  return { backdrop, wallTexture };
}

export function drawFrame(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  level: Level,
  corridors: readonly Rect[],
  cursor: Vec2 | null,
  hud: HudInfo,
  artBaseUrl: string,
): void {
  ctx.save();
  ctx.clearRect(0, 0, w, h);

  const { backdrop, wallTexture } = resolveArtForFrame(artBaseUrl, level);

  if (backdrop !== null) {
    ctx.drawImage(backdrop, 0, 0, w, h);
  } else {
    // Exactly today's coded look — never a blank/broken board when art is missing,
    // still loading, or failed to load.
    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, w, h);
  }

  ctx.strokeStyle = WALL_STROKE;
  ctx.lineWidth = 1;
  if (wallTexture !== null) {
    const pattern = ctx.createPattern(wallTexture, "repeat");
    ctx.fillStyle = pattern ?? WALL_FILL;
  } else {
    ctx.fillStyle = WALL_FILL;
  }
  for (const r of corridors) {
    ctx.fillRect(r.x, r.y, r.w, r.h);
    ctx.strokeRect(r.x + 0.5, r.y + 0.5, Math.max(0, r.w - 1), Math.max(0, r.h - 1));
  }

  ctx.fillStyle = START_FILL;
  ctx.fillRect(level.start.x, level.start.y, level.start.w, level.start.h);
  ctx.fillStyle = GOAL_FILL;
  ctx.fillRect(level.goal.x, level.goal.y, level.goal.w, level.goal.h);

  if (cursor !== null) {
    ctx.beginPath();
    ctx.fillStyle = CURSOR_FILL;
    ctx.arc(cursor.x, cursor.y, CURSOR_RADIUS, 0, Math.PI * 2);
    ctx.fill();
  }

  drawHud(ctx, hud);
  ctx.restore();
}

function drawHud(ctx: CanvasRenderingContext2D, hud: HudInfo): void {
  ctx.textBaseline = "top";
  ctx.font = "14px system-ui, sans-serif";
  ctx.fillStyle = HUD_TEXT;
  ctx.fillText(
    `${hud.levelName}  ·  ${hud.mode}  ·  ${hud.fps.toFixed(0)} fps  ·  scare=${hud.scareArmed}`,
    12,
    10,
  );

  ctx.font = "12px system-ui, sans-serif";
  ctx.fillStyle = HUD_MUTED;
  const pText = hud.focusP === null ? "—" : hud.focusP.toFixed(2);
  ctx.fillText(`focus p=${pText} thr=${hud.focusThreshold.toFixed(2)} ${hud.focusStatus}`, 12, 30);

  const barX = 12;
  const barY = 46;
  const barW = 160;
  const barH = 8;
  ctx.strokeStyle = HUD_MUTED;
  ctx.strokeRect(barX, barY, barW, barH);
  if (hud.focusP !== null) {
    ctx.fillStyle = hud.focusStatus === "ARMED" ? CURSOR_FILL : "#5dc8ff";
    ctx.fillRect(barX, barY, barW * Math.max(0, Math.min(1, hud.focusP)), barH);
  }

  ctx.fillStyle = HUD_MUTED;
  ctx.font = "11px system-ui, sans-serif";
  const gazeLine =
    hud.gaze === null
      ? "gaze raw=— cal=— path=—"
      : `gaze raw=(${hud.gaze.raw.x.toFixed(2)},${hud.gaze.raw.y.toFixed(2)})  cal=(${hud.gaze.calibrated.x.toFixed(2)},${hud.gaze.calibrated.y.toFixed(2)})  path=${hud.gaze.path}`;
  ctx.fillText(gazeLine, 12, 64);

  if (hud.telemetry !== null) {
    const t = hud.telemetry;
    ctx.fillText(
      `saccades ${t.saccades}  ·  blinks/min ${t.blinksPerMin.toFixed(1)}  ·  head ${t.headMotionDegPerS.toFixed(0)}°/s  ·  face ${(t.facePresentRatio * 100).toFixed(0)}%`,
      12,
      82,
    );
  }

  const s = hud.steer;
  const intentText = s === null ? "—" : (s.intent ?? "—");
  const confText = (s === null ? 0 : s.confidence).toFixed(2);
  const relText = (s === null ? 0 : s.reliable).toFixed(2);
  const assistText = (s === null ? 0 : s.assist).toFixed(2);
  const dampText = (s === null ? 1 : s.damp).toFixed(2);
  const reasonText = s === null ? "none" : s.reason;
  const ageText =
    s === null || s.ageMs === null ? "—" : Math.max(0, Math.round(s.ageMs)).toString();
  ctx.fillText(
    `intent=${intentText} conf=${confText} rel=${relText} assist=${assistText} damp=${dampText} ${reasonText} age=${ageText}ms`,
    12,
    100,
  );

  ctx.fillText(
    `drift=(${formatSigned(hud.drift.offsetX)},${formatSigned(hud.drift.offsetY)})  poll=${hud.drift.pollMs}ms`,
    12,
    118,
  );

  if (hud.banner !== null) {
    ctx.fillStyle = "#ffcf5c";
    ctx.font = "13px system-ui, sans-serif";
    ctx.textBaseline = "bottom";
    ctx.fillText(hud.banner, 12, ctx.canvas.height - 12);
  }
}
